// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, CheckCircle2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { getSessionUser } from '@/lib/authSession';

interface MasterTeam {
  id: string;
  organization_id: string | null;
  name: string;
  city: string | null;
  state: string | null;
  sport_id: string | null;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  description: string | null;
  website_url: string | null;
  social_instagram: string | null;
  social_twitter: string | null;
  is_active: boolean;
  is_verified: boolean;
}

const EMPTY: Partial<MasterTeam> = {
  name: "",
  city: "",
  state: "",
  sport_id: "",
  logo_url: "",
  primary_color: "#002E62",
  secondary_color: "#BC8D09",
  description: "",
  website_url: "",
  social_instagram: "",
  social_twitter: "",
  is_active: true,
  is_verified: false,
};

export default function MasterTeamsPage() {
  const { organization } = useOrganization();
  const orgId = organization.id;

  const [teams, setTeams] = useState<MasterTeam[]>([]);
  const [sports, setSports] = useState<any[]>([]);
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});
  const [filterSportId, setFilterSportId] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<MasterTeam> | null>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [newMember, setNewMember] = useState({ email: "", full_name: "", role: "coach", age_group: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    const [{ data: t }, { data: s }] = await Promise.all([
      supabase
        .from("master_teams")
        .select("*")
        .or(`organization_id.eq.${orgId},organization_id.is.null`)
        .order("name"),
      supabase.from("sports").select("id, name").order("name"),
    ]);
    setTeams(t ?? []);
    setSports(s ?? []);
    if (t && t.length > 0) {
      const ids = t.map((x) => x.id);
      const { data: mems } = await supabase
        .from("master_team_members")
        .select("master_team_id")
        .in("master_team_id", ids)
        .is("removed_at", null);
      const counts: Record<string, number> = {};
      (mems ?? []).forEach((m: any) => {
        counts[m.master_team_id] = (counts[m.master_team_id] ?? 0) + 1;
      });
      setMemberCounts(counts);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [orgId]);

  const loadMembers = async (teamId: string) => {
    const { data } = await supabase
      .from("master_team_members")
      .select("*")
      .eq("master_team_id", teamId)
      .is("removed_at", null)
      .order("created_at", { ascending: true } as any);
    setMembers(data ?? []);
  };

  const openEdit = (t: Partial<MasterTeam>) => {
    setEditing(t);
    setMembers([]);
    if (t.id) loadMembers(t.id);
  };

  const filtered = useMemo(() => {
    return teams.filter((t) => {
      if (filterSportId !== "all" && t.sport_id !== filterSportId) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !t.name.toLowerCase().includes(q) &&
          !(t.city ?? "").toLowerCase().includes(q) &&
          !(t.state ?? "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [teams, filterSportId, search]);

  const save = async () => {
    if (!editing || !orgId) return;
    if (!editing.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!editing.sport_id) {
      toast.error("Sport is required");
      return;
    }
    const payload = {
      organization_id: editing.organization_id ?? orgId,
      name: editing.name.trim(),
      city: editing.city || null,
      state: editing.state || null,
      sport_id: editing.sport_id,
      logo_url: editing.logo_url || null,
      primary_color: editing.primary_color || null,
      secondary_color: editing.secondary_color || null,
      description: editing.description || null,
      website_url: editing.website_url || null,
      social_instagram: editing.social_instagram || null,
      social_twitter: editing.social_twitter || null,
      is_active: editing.is_active ?? true,
      is_verified: editing.is_verified ?? false,
    };
    let error;
    let savedId = editing.id;
    if (editing.id) {
      ({ error } = await supabase.from("master_teams").update(payload).eq("id", editing.id));
    } else {
      const { data: u } = await getSessionUser();
      const { data, error: insErr } = await supabase
        .from("master_teams")
        .insert({ ...payload, created_by: u?.user?.id ?? null })
        .select("id")
        .single();
      error = insErr;
      savedId = data?.id;
    }
    if (error) {
      toast.error("Failed to save: " + error.message);
      return;
    }
    toast.success("Saved");
    setEditing(savedId ? { ...editing, id: savedId } : null);
    load();
  };

  const remove = async (t: MasterTeam) => {
    if (!confirm(`Delete ${t.name}? This cannot be undone.`)) return;
    const { error } = await supabase.from("master_teams").delete().eq("id", t.id);
    if (error) toast.error("Failed: " + error.message);
    else {
      toast.success("Deleted");
      load();
    }
  };

  const uploadLogo = async (file: File) => {
    if (!editing) return;
    const ext = file.name.split(".").pop();
    const path = `${editing.id ?? "new"}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("master-team-logos").upload(path, file, { upsert: true });
    if (error) {
      toast.error("Upload failed: " + error.message);
      return;
    }
    const { data } = supabase.storage.from("master-team-logos").getPublicUrl(path);
    setEditing({ ...editing, logo_url: data.publicUrl });
    toast.success("Logo uploaded");
  };

  const addMember = async () => {
    if (!editing?.id || !newMember.email.trim()) return;
    // TODO: send invite email — for now insert with joined_at=null
    const { error } = await supabase.from("master_team_members").insert({
      master_team_id: editing.id,
      email: newMember.email.trim().toLowerCase(),
      full_name: newMember.full_name || null,
      role: newMember.role,
      age_group: newMember.age_group || null,
      joined_at: null,
    });
    if (error) {
      toast.error("Failed to add: " + error.message);
      return;
    }
    setNewMember({ email: "", full_name: "", role: "coach", age_group: "" });
    loadMembers(editing.id);
  };

  const removeMember = async (id: string) => {
    if (!confirm("Remove member?")) return;
    const { error } = await supabase
      .from("master_team_members")
      .update({ removed_at: new Date().toISOString() })
      .eq("id", id);
    if (error) toast.error("Failed: " + error.message);
    else if (editing?.id) loadMembers(editing.id);
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Master Teams</h1>
          <p className="text-sm text-muted-foreground">
            Canonical team identities used across registrations. Teams can have multiple age groups.
          </p>
        </div>
        <Button onClick={() => openEdit({ ...EMPTY })}>
          <Plus className="w-4 h-4 mr-1" /> Add Master Team
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={filterSportId}
          onChange={(e) => setFilterSportId(e.target.value)}
          className="border border-input rounded px-2 py-1 text-sm"
        >
          <option value="all">All sports</option>
          {sports.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <div className="relative ml-auto">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, city, state…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 w-72"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
          No master teams yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((t) => {
            const sport = sports.find((s) => s.id === t.sport_id);
            return (
              <button
                key={t.id}
                onClick={() => openEdit(t)}
                className="text-left p-4 border border-border rounded-xl bg-white hover:border-primary transition-colors"
              >
                <div className="flex items-start gap-3">
                  {t.logo_url ? (
                    <img src={t.logo_url} alt="" className="w-12 h-12 rounded object-cover" />
                  ) : (
                    <div
                      className="w-12 h-12 rounded flex items-center justify-center text-white font-bold text-lg"
                      style={{ background: t.primary_color || "#002E62" }}
                    >
                      {t.name[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <h3 className="font-semibold truncate">{t.name}</h3>
                      {t.is_verified && <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {[t.city, t.state].filter(Boolean).join(", ") || "—"}
                    </p>
                    {sport && (
                      <span className="inline-block mt-1 text-[10px] px-2 py-0.5 bg-secondary rounded uppercase tracking-wide">
                        {sport.name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t text-xs text-muted-foreground flex justify-between">
                  <span>{memberCounts[t.id] ?? 0} members</span>
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(t);
                    }}
                    className="text-muted-foreground hover:text-red-700 cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5 inline" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Master Team" : "Add Master Team"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <Tabs defaultValue="details">
              <TabsList>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="members" disabled={!editing.id}>
                  Members
                </TabsTrigger>
                <TabsTrigger value="waivers" disabled={!editing.id}>
                  Waivers
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4 pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium">Name *</label>
                    <Input
                      value={editing.name ?? ""}
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Sport *</label>
                    <select
                      value={editing.sport_id ?? ""}
                      onChange={(e) => setEditing({ ...editing, sport_id: e.target.value })}
                      className="w-full border border-input rounded px-2 py-2 text-sm"
                    >
                      <option value="">Select…</option>
                      {sports.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium">City</label>
                    <Input
                      value={editing.city ?? ""}
                      onChange={(e) => setEditing({ ...editing, city: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium">State</label>
                    <Input
                      value={editing.state ?? ""}
                      onChange={(e) => setEditing({ ...editing, state: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium block mb-1">Logo</label>
                  <div className="flex items-center gap-3">
                    {editing.logo_url ? (
                      <img src={editing.logo_url} alt="" className="w-16 h-16 rounded object-cover" />
                    ) : (
                      <div className="w-16 h-16 rounded bg-muted flex items-center justify-center text-muted-foreground text-xs">
                        No logo
                      </div>
                    )}
                    <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-input rounded text-sm hover:bg-muted">
                      <Upload className="w-4 h-4" />
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])}
                      />
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium">Primary color</label>
                    <Input
                      type="color"
                      value={editing.primary_color ?? "#002E62"}
                      onChange={(e) => setEditing({ ...editing, primary_color: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Secondary color</label>
                    <Input
                      type="color"
                      value={editing.secondary_color ?? "#BC8D09"}
                      onChange={(e) => setEditing({ ...editing, secondary_color: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium">Description</label>
                  <Textarea
                    rows={3}
                    value={editing.description ?? ""}
                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium">Website</label>
                    <Input
                      value={editing.website_url ?? ""}
                      onChange={(e) => setEditing({ ...editing, website_url: e.target.value })}
                      placeholder="https://"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Instagram</label>
                    <Input
                      value={editing.social_instagram ?? ""}
                      onChange={(e) => setEditing({ ...editing, social_instagram: e.target.value })}
                      placeholder="@handle"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Twitter</label>
                    <Input
                      value={editing.social_twitter ?? ""}
                      onChange={(e) => setEditing({ ...editing, social_twitter: e.target.value })}
                      placeholder="@handle"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editing.is_active ?? true}
                      onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                    />
                    Active
                  </label>
                  {/* TODO: only show Verified toggle for super-admins */}
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editing.is_verified ?? false}
                      onChange={(e) => setEditing({ ...editing, is_verified: e.target.checked })}
                    />
                    Verified
                  </label>
                </div>
              </TabsContent>

              <TabsContent value="members" className="space-y-4 pt-3">
                <div className="space-y-2">
                  {members.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No members yet.</p>
                  ) : (
                    <div className="border rounded-lg divide-y">
                      {members.map((m) => (
                        <div key={m.id} className="px-3 py-2 flex items-center gap-3 text-sm">
                          <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold">
                            {(m.full_name ?? m.email)?.[0]?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{m.full_name || m.email}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {m.email} • {m.role}
                              {m.age_group ? ` • ${m.age_group}` : ""}
                              {!m.joined_at && " • Pending"}
                            </div>
                          </div>
                          <button
                            onClick={() => removeMember(m.id)}
                            className="p-1 text-muted-foreground hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border rounded-lg p-3 space-y-2">
                  <div className="text-sm font-medium">Add member</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Email *"
                      value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    />
                    <Input
                      placeholder="Full name"
                      value={newMember.full_name}
                      onChange={(e) => setNewMember({ ...newMember, full_name: e.target.value })}
                    />
                    <select
                      value={newMember.role}
                      onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                      className="border border-input rounded px-2 text-sm"
                    >
                      <option value="coach">Coach</option>
                      <option value="assistant_coach">Assistant Coach</option>
                      <option value="manager">Manager</option>
                      <option value="parent">Parent</option>
                    </select>
                    <Input
                      placeholder="Age group (e.g. 14U Boys)"
                      value={newMember.age_group}
                      onChange={(e) => setNewMember({ ...newMember, age_group: e.target.value })}
                    />
                  </div>
                  <Button size="sm" onClick={addMember}>
                    Add
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="waivers" className="space-y-3 pt-3">
                <MasterTeamWaiversTab masterTeamId={editing.id} />
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Close
            </Button>
            <Button onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MasterTeamWaiversTab({ masterTeamId }: { masterTeamId: string }) {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!masterTeamId) return;
    (async () => {
      setLoading(true);
      const { data, error } = await (supabase as any)
        .from("event_waiver_signatures")
        .select("*, events:event_id(name), waiver_catalog:waiver_catalog_id(title, waiver_type)")
        .eq("master_team_id", masterTeamId)
        .order("signed_at", { ascending: false });
      if (error) toast.error(error.message);
      setRows(data ?? []);
      setLoading(false);
    })();
  }, [masterTeamId]);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-xs text-muted-foreground">Signatures collected for this team across all events.</p>
        <Button size="sm" variant="outline" disabled>Download all</Button>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-6 text-center">No signatures recorded.</p>
      ) : (
        <div className="border border-border rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left">
              <tr>
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">Waiver</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Signed By</th>
                <th className="px-3 py-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2">{r.events?.name ?? "—"}</td>
                  <td className="px-3 py-2">{r.waiver_catalog?.title ?? "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.waiver_type ?? "—"}</td>
                  <td className="px-3 py-2">{r.signer_name}</td>
                  <td className="px-3 py-2 text-xs">{new Date(r.signed_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
